/**
 * Centralized UTM parameter utilities
 * Single point of truth for UTM parameter handling
 */

import { getCookie } from './cookie-utils';

/**
 * UTM parameters interface
 */
export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  utm_group?: string;
  utm_creative?: string;
  utm_fbclid?: string;
  utm_id?: string;
  ip?: string; // IPv4 or IPv6 address
  userAgent?: string; // Browser user agent string
  targetUrl?: string; // The page they signed up
  utm_so_fast?:any
}

/**
 * List of UTM parameter keys to capture
 * Single source of truth for all UTM parameters
 */
export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_group',
  'utm_creative',
  'utm_fbclid',
  'utm_id',
  "ip",
  "userAgent",
  "targetUrl",
  "utm_so_fast"
] as const;

/**
 * Extract UTM parameters from current URL
 * @returns Object containing found UTM parameters
 */
export function getUTMParamsFromURL(): UTMParams {
  if (typeof window === 'undefined') {
    return {};
  }

  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: UTMParams = {};

  // Extract UTM parameters from URL
  UTM_KEYS.forEach((key) => {
    const value = urlParams.get(key);
    if (value) {
      utmParams[key as keyof UTMParams] = value;
    }
  });

  // Handle fbclid parameter: if utm_fbclid is a placeholder, use fbclid instead
  const utmFbclid = utmParams.utm_fbclid;
  const fbclid = urlParams.get('fbclid');
  
  // Check if utm_fbclid is a placeholder (like {fbclid} or %7Bfbclid%7D)
  const isPlaceholder = utmFbclid && (
    utmFbclid === '{fbclid}' || 
    utmFbclid === '%7Bfbclid%7D' ||
    utmFbclid.toLowerCase() === '{fbclid}' ||
    decodeURIComponent(utmFbclid) === '{fbclid}'
  );
  
  // If utm_fbclid is a placeholder and we have a real fbclid, use the real one
  if (isPlaceholder && fbclid) {
    utmParams.utm_fbclid = fbclid;
  } else if (!utmFbclid && fbclid) {
    // If no utm_fbclid but we have fbclid, use it
    utmParams.utm_fbclid = fbclid;
  } else if (isPlaceholder) {
    // If it's a placeholder but no real fbclid, remove it
    delete utmParams.utm_fbclid;
  }

  // Always set targetUrl to the current URL
  utmParams.targetUrl = window.location.href;
  
  // Always capture user agent (available directly in browser)
  const userAgent = getUserAgent();
  if (userAgent) {
    utmParams.userAgent = userAgent;
  }

  return utmParams;
}

/**
 * Get user agent string from browser
 * @returns User agent string or null if not available
 */
export function getUserAgent(): string | null {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return null;
  }
  return navigator.userAgent || null;
}

/**
 * Check if an IP address is localhost/private (should be filtered out)
 * @param ip - IP address to check
 * @returns true if IP is localhost or private
 */
function isLocalhostOrPrivateIP(ip: string): boolean {
  if (!ip) return true;
  
  // Check for localhost variants
  const localhostPatterns = [
    /^127\./,
    /^::ffff:127\./,
    /^::1$/,
    /^localhost$/i,
    /^0\.0\.0\.0$/,
    /^::ffff:0\.0\.0\.0$/,
  ];
  
  if (localhostPatterns.some(pattern => pattern.test(ip))) {
    return true;
  }
  
  // Check for private IP ranges (RFC 1918)
  const privateRanges = [
    /^10\./,           // 10.0.0.0/8
    /^172\.(1[6-9]|2[0-9]|3[01])\./, // 172.16.0.0/12
    /^192\.168\./,     // 192.168.0.0/16
    /^169\.254\./,     // Link-local (169.254.0.0/16)
  ];
  
  if (privateRanges.some(pattern => pattern.test(ip))) {
    return true;
  }
  
  return false;
}

/**
 * Fetch user's IP address from Cloudflare Worker (server-side) or fallback to client-side API
 * @returns Promise that resolves to IP address string or null if failed or localhost
 */
export async function fetchIPAddress(): Promise<string | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  // Try Cloudflare Worker function first (server-side, more reliable)
  try {
    // Use relative path for same-origin, or full URL if needed
    const workerUrl = '/api/get-visitor-ip';
    console.log("🚀 ~ fetchIPAddress ~ workerUrl:", workerUrl)
    const response = await fetch(workerUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
    console.log("🚀 ~ fetchIPAddress ~ response:", response)

    if (response.ok) {
      const data = await response.json();
      const ip = data.ip || null;
      
      // Filter out localhost/private IPs (shouldn't happen from server, but safety check)
      if (ip && !isLocalhostOrPrivateIP(ip)) {
        return ip;
      } else if (ip) {
        console.warn('IP address from worker is localhost/private, not storing:', ip);
        // Continue to fallback
      }
    }
  } catch (error) {
    console.warn('Failed to fetch IP address from Cloudflare Worker:', error);
    // Continue to fallback
  }

  // Fallback to client-side API (less reliable but works if worker is unavailable)
  try {
    // Try ipify.org (simple and reliable)
    const response = await fetch('https://api.ipify.org?format=json', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      const ip = data.ip || null;
      
      // Filter out localhost/private IPs
      if (ip && !isLocalhostOrPrivateIP(ip)) {
        return ip;
      } else if (ip) {
        console.warn('IP address is localhost/private, not storing:', ip);
        return null;
      }
    }
  } catch (error) {
    console.warn('Failed to fetch IP address from ipify:', error);
    
    // Final fallback to alternative service
    try {
      const fallbackResponse = await fetch('https://api.ipapi.com/api/check?access_key=free', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (fallbackResponse.ok) {
        const data = await fallbackResponse.json();
        const ip = data.ip || null;
        
        // Filter out localhost/private IPs
        if (ip && !isLocalhostOrPrivateIP(ip)) {
          return ip;
        } else if (ip) {
          console.warn('IP address is localhost/private, not storing:', ip);
          return null;
        }
      }
    } catch (fallbackError) {
      console.warn('Failed to fetch IP address from fallback service:', fallbackError);
    }
  }

  return null;
}

/**
 * Get UTM data from cookies
 * @returns Object containing UTM parameters from cookies
 */
export function getUTMDataFromCookies(): UTMParams {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const utmDataCookie = getCookie('utm_data');
    if (utmDataCookie) {
      const utmData = JSON.parse(utmDataCookie) as UTMParams;
      // Always ensure targetUrl is set to current URL (update if missing or outdated)
      utmData.targetUrl = window.location.href;
      // Always ensure user agent is current (it can change if user switches devices/browsers)
      const userAgent = getUserAgent();
      if (userAgent) {
        utmData.userAgent = userAgent;
      }
      return utmData;
    }
  } catch (error) {
    console.warn('Failed to parse UTM data from cookies:', error);
  }

  // If no cookie exists, try to get UTM params from current URL as fallback
  // This ensures we don't lose UTM data if cookie wasn't set yet
  const urlParams = getUTMParamsFromURL();
  const userAgent = getUserAgent();
  
  return {
    ...urlParams, // This includes all UTM params from URL, targetUrl, and userAgent
    targetUrl: window.location.href, // Always use current URL
    ...(userAgent && { userAgent })
  };
}

